'use client';

import SupportActionsRenderer from '@c/dashboard/components/SupportActionsRenderer';
import MessageBubble from '@c/dashboard/MessageBubble';
import { useOffers } from '@c/dashboard/useOffers';
import { LogoWithoutText } from '@c/icons/logoWithoutText';
import BaseModal from '@c/modals/BaseModal';
import ProductViewerModal from '@c/modals/ProductViewerModal';
import { useQuery } from '@tanstack/react-query';
import Avatar from '@ui/Avatar';
import {
  getChatById,
  getLastOfferSenderId,
  sendMessageToChat,
  uploadChatImage,
} from '@util/firestore/messages';
import { getOrderById } from '@util/firestore/orders';
import { getProductById } from '@util/firestore/products';
import { subscribeToPendingApproval } from '@util/firestore/support/support.service';
import { getPublicUserDoc, getUserById } from '@util/firestore/users';
import { anyAreHeic, convertHeic } from '@util/heic';
import { useConversationMutations } from '@util/hooks/useConversationMutations';
import { StatusLabel, useOnlineStatus } from '@util/hooks/useOnlineStatus';
import { cn, isMobile, isTablet } from '@util/index';
import { logError } from '@util/logError';
import {
  canUseRequestPayment,
  hasSupportPermission,
  MX_LOCKER_ACCOUNT_UID,
} from '@util/support';
import { getCdnImageUrls } from '@util/urlHelpers';
import { generateVideoThumbnail } from '@util/videoThumbnail';
import VerifiedShieldIcon from 'app/profile/[username]/components/icons/VerifiedShieldIcon';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import { PendingApprovalBubble } from 'features/support-bot/components/PendingApprovalBubble';
import type { PendingApproval } from 'features/support-bot/components/SupportBotButton';
import { onSnapshot } from 'firebase/firestore';
import { LayoutGroup, motion } from 'framer-motion';
import {
  ChevronLeftIcon,
  CloudUploadIcon,
  MoreHorizontalIcon,
  StarIcon,
} from 'lucide-react';
import type { ChatDocument, ChatMessageDocument } from 'models/chat';
import { isVehicleCategory } from 'models/product';
import Link from 'next/link';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ChatDetailsModal from './ChatDetails/ChatDetailsModal';
import MobileChatDetailsModal from './ChatDetails/MobileChatDetailsModal';
import ChatInput from './ChatInput';
import ChatOfferButton from './ChatOfferButton';
import ChatPreviewSidebar from './ChatPreview/ChatPreviewSidebar';
import MobileChatPreview from './ChatPreview/MobileChatPreview';
import MessageImageSkeleton from './MessageImageSkeleton';
import SupportStatusBar from './SupportStatusBar';

type ChatWindowProps = {
  chatId: string;
  searchTerm?: string;
  onChatArchived?: () => void;
  onGoBack?: () => void;
  isSidebar?: boolean;
};

export default function ChatWindow({
  chatId,
  searchTerm,
  onChatArchived,
  onGoBack,
  isSidebar,
}: ChatWindowProps) {
  const dragCounter = useRef<number>(0);
  const [messages, setMessages] = useState<ChatMessageDocument[] | null>(null);
  const [realtimeChat, setRealtimeChat] = useState<ChatDocument | null>(null);
  const [showChatDetailsModal, setShowChatDetailsModal] = useState(false);
  const [showMobileChatDetailsModal, setShowMobileChatDetailsModal] =
    useState(false);
  const [showSupportPreviewModal, setShowSupportPreviewModal] = useState(false);
  const [inputMessage, setInputMessage] = useState<string>('');
  const [fileList, setFileList] = useState<File[]>([]);
  const [convertingImages, setConvertingImages] = useState(false);
  const [uploadingImages, setUploadingImages] = useState<
    Array<{ id: string; file: File }>
  >([]);
  const [isDragging, setIsDragging] = useState(false);
  const [loadedChatPreviews, setLoadedChatPreviews] = useState<Set<string>>(
    new Set()
  );
  const [imageModal, setImageModal] = useState<{
    isOpen: boolean;
    index: number;
    images: { full: string; thumb: string; download_url?: string }[];
  }>({ isOpen: false, index: 0, images: [] });
  const [pendingApproval, setPendingApproval] =
    useState<PendingApproval | null>(null);
  const messageWindowRef = useRef<HTMLDivElement>(null);
  const messageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const inputId = useRef<string>(Math.random().toString(36).substring(7));
  const chatInputRef = useRef<HTMLTextAreaElement>(null);

  const { userDoc } = useAuth();
  const { showErrorToast } = useToastContext();

  const { data: sellerDoc, isLoading: isSellerLoading } = useQuery({
    queryKey: ['publicUser', realtimeChat?.seller_id],
    queryFn: () => getPublicUserDoc({ uid: realtimeChat?.seller_id }),
    enabled: !!realtimeChat?.seller_id,
  });

  const { data: buyerDoc, isLoading: isBuyerLoading } = useQuery({
    queryKey: ['publicUser', realtimeChat?.buyer_id],
    queryFn: () => getPublicUserDoc({ uid: realtimeChat?.buyer_id }),
    enabled: !!realtimeChat?.buyer_id,
  });

  const { data: product, isLoading: isProductLoading } = useQuery({
    queryKey: ['product', realtimeChat?.product_id ?? ''],
    queryFn: () => getProductById(realtimeChat?.product_id ?? ''),
    enabled: !!realtimeChat?.product_id,
  });

  const { offer, isLoading: isOfferLoading } = useOffers(
    realtimeChat?.offer_id
  );

  const { data: order, isLoading: isOrderLoading } = useQuery({
    queryKey: ['order', realtimeChat?.order_id ?? ''],
    queryFn: () => getOrderById(realtimeChat?.order_id ?? ''),
    enabled: !!realtimeChat?.order_id,
  });

  // Find matching messages based on search term
  const matchingMessageIndices = useMemo(() => {
    if (!searchTerm || !messages) return [];

    const indices: number[] = [];
    const lowercaseSearchTerm = searchTerm.toLowerCase();

    messages.forEach((message, index) => {
      if (message.content?.toLowerCase().includes(lowercaseSearchTerm)) {
        indices.push(index);
      }
    });

    return indices;
  }, [messages, searchTerm]);

  // Extract images from chat messages
  const chatImages = useMemo(() => {
    if (!messages) return [];

    return messages
      .filter((message) => message.is_img && message.content)
      .map((message) => message.content as string);
  }, [messages]);

  // Subscribe to real-time messages
  const chatsQuery = useMemo(() => getChatById(chatId ?? ''), [chatId]);

  useEffect(() => {
    if (!chatId) return;

    const unsubscribe = onSnapshot(chatsQuery, (snapshot) => {
      const data = snapshot.data();
      if (data) {
        // Update the complete chat document
        setRealtimeChat({ ...data, id: snapshot.id } as ChatDocument);
        setMessages(data?.messages ?? []);

        // Scroll to bottom when new messages arrive
        setTimeout(() => {
          if (messageWindowRef.current) {
            messageWindowRef.current.scrollTop =
              messageWindowRef.current.scrollHeight;
          }
        }, 100);
      } else {
        setRealtimeChat(null);
        setMessages([]);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [chatsQuery, chatId]);

  // Subscribe to real-time pending approvals for this chat
  useEffect(() => {
    if (!chatId || !userDoc?.roles?.support) return;

    const unsubscribe = subscribeToPendingApproval(chatId, (approval) => {
      if (approval) {
        setPendingApproval({
          approval_id: approval.id,
          message: approval.proposed_message,
          actions: approval.proposed_actions,
          task_id: approval.task_id,
          internal: approval.internal,
        });
      } else {
        setPendingApproval(null);
      }
    });

    return () => unsubscribe();
  }, [chatId, userDoc?.roles?.support]);

  // Scroll to first matching message when search term changes or messages load
  useEffect(() => {
    if (
      matchingMessageIndices.length > 0 &&
      messages &&
      messageWindowRef.current &&
      searchTerm
    ) {
      const firstMatchIndex = matchingMessageIndices[0];
      const messageElement = messageRefs.current[firstMatchIndex];

      if (messageElement) {
        setTimeout(() => {
          messageElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }, 300); // Small delay to ensure messages are rendered
      }
    }
  }, [matchingMessageIndices, messages, searchTerm]);

  const { starChat } = useConversationMutations();
  const onImageClick = (src: string) => {
    let index: number | undefined;
    const images: { thumb: string; full: string; download_url?: string }[] = [];

    messages
      ?.filter((m) => m.is_img)
      .forEach((msg, i) => {
        if (
          index === undefined &&
          (src === msg.thumbnail || src === msg.content)
        ) {
          index = i;
        }
        images.push({
          thumb: msg.thumbnail ?? msg.content,
          full: msg.content,
          download_url: msg.download_url,
        });
      });

    if (index === undefined) index = 0;
    setImageModal({ images, index, isOpen: true });
  };

  const handleSendMessage = async () => {
    if (!userDoc || !realtimeChat?.id || convertingImages) return;

    // if (
    //   userDoc?.roles?.support &&
    //   !userDoc.roles.admin &&
    //   !realtimeChat.case_num &&
    //   !realtimeChat.uids?.includes(userDoc.uid) &&
    //   !realtimeChat.messages[0]?.content?.startsWith('Return requested')
    // ) {
    //   showErrorToast('You are not authorized to send messages to this chat');
    //   return;
    // }

    // Send text message if there's text content
    if (inputMessage.trim()) {
      const messageDoc: ChatMessageDocument = {
        uid: userDoc.uid,
        content: inputMessage.trim(),
        created_at: Date.now(),
        is_img: false,
        is_auto: false,
      };
      sendMessageToChat(
        messageDoc,
        realtimeChat,
        userDoc,
        product || undefined
      );
    }

    // Send image messages if there are files
    if (fileList.length > 0) {
      const files = Array.from(fileList);

      // Create optimistic uploading images
      const newUploadingImages = files.map((file) => ({
        id: `${Date.now()}-${Math.random().toString(36).substring(7)}`,
        file,
      }));

      setUploadingImages((prev) => [...prev, ...newUploadingImages]);

      // Process each file
      files.forEach(async (file, index) => {
        const uploadingId = newUploadingImages[index].id;
        const isVideo = file.type.startsWith('video/');
        try {
          if (isVideo) {
            let thumbnailCdnUrl = '';
            try {
              const thumbBlob = await generateVideoThumbnail(file);
              if (thumbBlob) {
                const thumbResult = await uploadChatImage(
                  thumbBlob,
                  userDoc.uid,
                  realtimeChat.id
                );
                thumbnailCdnUrl = getCdnImageUrls(thumbResult.path).thumb;
              }
            } catch (thumbError) {
              logError(thumbError);
            }

            const videoResult = await uploadChatImage(
              file,
              userDoc.uid,
              realtimeChat.id
            );

            // Videos go through the Firebase Storage download URL, not the
            // Bunny image CDN — the CDN strips/loses Content-Type and Range
            // support for non-image assets, which makes the player stall.
            const videoMessageDoc: ChatMessageDocument = {
              uid: userDoc.uid,
              content: videoResult.download_url,
              thumbnail: thumbnailCdnUrl,
              download_url: videoResult.download_url,
              mime_type: file.type,
              created_at: Date.now(),
              is_img: true,
              is_auto: false,
            };

            await sendMessageToChat(
              videoMessageDoc,
              realtimeChat,
              userDoc,
              product || undefined
            );
          } else {
            const uploadResult = await uploadChatImage(
              file,
              userDoc.uid,
              realtimeChat.id
            );
            const { path, download_url } = uploadResult;
            const { full, thumb } = getCdnImageUrls(path);

            const imageMessageDoc: ChatMessageDocument = {
              uid: userDoc.uid,
              content: full,
              thumbnail: thumb,
              download_url: download_url,
              created_at: Date.now(),
              is_img: true,
              is_auto: false,
            };

            await sendMessageToChat(
              imageMessageDoc,
              realtimeChat,
              userDoc,
              product || undefined
            );
          }

          // Remove from uploading state
          setUploadingImages((prev) =>
            prev.filter((img) => img.id !== uploadingId)
          );
        } catch (error) {
          logError(error);
          showErrorToast(
            isVideo ? 'Failed to upload video' : 'Failed to upload image'
          );
          // Remove from uploading state on error
          setUploadingImages((prev) =>
            prev.filter((img) => img.id !== uploadingId)
          );
        }
      });
    }

    // Reset form
    setInputMessage('');
    setFileList([]);
  };

  const handleQuickReply = useCallback(
    (text: string) => {
      if (!userDoc || !realtimeChat?.id) return;

      const messageDoc: ChatMessageDocument = {
        uid: userDoc.uid,
        content: text,
        created_at: Date.now(),
        is_img: false,
        is_auto: false,
      };
      sendMessageToChat(
        messageDoc,
        realtimeChat,
        userDoc,
        product || undefined
      );
    },
    [userDoc, realtimeChat, product]
  );

  const handleAttachment = () => {
    const input = document.querySelector(
      `#file-input-${inputId.current}`
    ) as HTMLInputElement;
    if (input) {
      input.click();
    }
  };

  const handlePreviewImageClick = (index: number) => {
    if (fileList[index]?.type.startsWith('video/')) return;
    const previewImages = fileList
      .filter((file) => !file.type.startsWith('video/'))
      .map((file) => {
        const url = URL.createObjectURL(file);
        return { full: url, thumb: url, download_url: url };
      });
    const adjustedIndex = fileList
      .slice(0, index)
      .filter((f) => !f.type.startsWith('video/')).length;
    setImageModal({
      images: previewImages,
      index: adjustedIndex,
      isOpen: true,
    });
  };

  const processFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;

      const newFiles = Array.from(files);
      const imageFiles = newFiles.filter((f) => f.type.startsWith('image/'));
      const anyHeic = anyAreHeic(imageFiles);
      const { heicTo } = anyHeic ? await import('heic-to') : {};

      if (anyHeic && heicTo) {
        setConvertingImages(true);
        const convertedFiles = await Promise.all(
          newFiles.map(async (file) => {
            if (/image\/hei(c|f)/.test(file.type)) {
              try {
                file = await convertHeic(heicTo, file);
              } catch (e) {
                logError(e);
                showErrorToast('Error converting HEIC image');
              }
            }
            return file;
          })
        );
        setFileList((prevFiles) => [...prevFiles, ...convertedFiles]);
        setConvertingImages(false);
      } else {
        setFileList((prevFiles) => [...prevFiles, ...newFiles]);
      }
    },
    [showErrorToast]
  );

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    await processFiles(Array.from(e.target.files));

    // Clear the file input so the same file can be selected again if needed
    e.target.value = '';
  };

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current = 0;
      setIsDragging(false);

      const files = Array.from(e.dataTransfer.files);
      const mediaFiles = files.filter(
        (file) =>
          file.type.startsWith('image/') || file.type.startsWith('video/')
      );

      if (mediaFiles.length > 0) {
        await processFiles(mediaFiles);
      }
    },
    [processFiles]
  );

  const isSupportChat =
    realtimeChat?.support_state !== undefined ||
    !!realtimeChat?.case_num ||
    hasSupportPermission(realtimeChat?.seller_id) ||
    hasSupportPermission(realtimeChat?.buyer_id);
  const isLoggedInAsSupport = userDoc?.roles?.support || userDoc?.roles?.admin;
  const isAdmin = userDoc?.roles?.admin || false;
  const { data: sellerFullDoc } = useQuery({
    queryKey: ['user', realtimeChat?.seller_id],
    queryFn: () => getUserById(realtimeChat?.seller_id ?? ''),
    enabled: !!realtimeChat?.seller_id && !!isAdmin,
  });

  const { data: buyerFullDoc } = useQuery({
    queryKey: ['user', realtimeChat?.buyer_id],
    queryFn: () => getUserById(realtimeChat?.buyer_id ?? ''),
    enabled: !!realtimeChat?.buyer_id && !!isAdmin,
  });

  const otherPartyUser =
    realtimeChat?.seller_id === userDoc?.uid ? buyerDoc : sellerDoc;
  const isMobileDevice = isMobile() || isTablet();
  const hasPreview = !!product || !!offer || !!order;
  const isTalkingToDeletedUser =
    sellerDoc?.username.startsWith('deleted-') ||
    buyerDoc?.username.startsWith('deleted-');

  const { status } = useOnlineStatus(otherPartyUser?.uid ?? '');
  const { status: sellerStatus } = useOnlineStatus(
    isAdmin ? sellerDoc?.uid ?? '' : ''
  );
  const { status: buyerStatus } = useOnlineStatus(
    isAdmin ? buyerDoc?.uid ?? '' : ''
  );

  // Track which chats have had their preview loaded to prevent re-animation
  const chatPreviewKey = `${realtimeChat?.id}-${
    product?.id || offer?.id || order?.id
  }`;
  const isFirstTimeLoading =
    hasPreview && !loadedChatPreviews.has(chatPreviewKey);

  const showBootTradeInPreviewMessages =
    userDoc?.uid === MX_LOCKER_ACCOUNT_UID &&
    realtimeChat?.messages?.[0]?.content.includes('New Boot Trade-In Request');
  const showSupportPreviewMessages = isLoggedInAsSupport && isSupportChat;

  // Mark chat preview as loaded when it appears for the first time
  useEffect(() => {
    if (hasPreview && !loadedChatPreviews.has(chatPreviewKey)) {
      setLoadedChatPreviews((prev) => new Set(prev).add(chatPreviewKey));
    }
  }, [hasPreview, chatPreviewKey, loadedChatPreviews]);

  // Auto-focus chat input when chat changes
  useEffect(() => {
    const timer = setTimeout(() => {
      chatInputRef.current?.focus();
    }, 100);

    return () => clearTimeout(timer);
  }, [realtimeChat?.id]);

  if (!realtimeChat) return null;
  return (
    <LayoutGroup>
      <div
        className={cn(
          'relative flex h-full bg-white',
          isSidebar ? 'w-[55rem]' : 'w-full'
        )}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {/* Left Column - Chat Messages */}
        <motion.div
          layout
          className="relative flex h-full flex-1 flex-col"
          transition={{
            type: 'spring',
            stiffness: 200,
            damping: 25,
            duration: 0.8,
          }}
        >
          <div
            className={cn(
              'h-fit bg-white px-4 py-6 sm:p-8',
              !hasPreview && 'border-b border-brand-lightest-gray'
            )}
          >
            <div className="flex items-center gap-4">
              {/* Back button */}
              {onGoBack && (
                <button
                  type="button"
                  onClick={onGoBack}
                  className="flex h-12 w-12 items-center justify-center rounded-full hover:bg-zinc-100"
                  aria-label="Go back to conversations"
                >
                  <ChevronLeftIcon className="h-8 w-8 text-zinc-600" />
                </button>
              )}
              <div className="flex flex-1 items-center gap-4">
                {isAdmin ? (
                  <div className="flex flex-1 flex-col gap-4 md:flex-row md:gap-24">
                    {/* Seller Section */}
                    <Link
                      href={`/profile/${sellerDoc?.username_slug}`}
                      target="_blank"
                      className="group flex items-center gap-4"
                      aria-label="View seller profile"
                    >
                      {isSellerLoading || !sellerDoc ? (
                        <div className="h-24 w-24 animate-pulse rounded-full bg-zinc-100" />
                      ) : (
                        <Avatar user={sellerDoc} circle className="h-24 w-24" />
                      )}

                      {isSellerLoading ? (
                        <div className="flex flex-col gap-4">
                          <div className="h-4 w-48 animate-pulse rounded-lg bg-zinc-200" />
                          <div className="flex items-center gap-1">
                            <div className="h-5 w-5 animate-pulse rounded-full bg-zinc-200" />
                            <div className="h-4 w-64 animate-pulse rounded-lg bg-zinc-200" />
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col gap-y-2">
                          <div className="flex items-center gap-2">
                            <span className="text-[1.4rem] font-medium text-zinc-500">
                              Seller:
                            </span>
                            <h3
                              className={cn(
                                'max-w-[30rem] truncate text-left text-[1.6rem] font-semibold underline',
                                sellerDoc?.username.startsWith('deleted-') &&
                                  'text-red-600'
                              )}
                            >
                              {sellerDoc?.username.startsWith('deleted-')
                                ? 'Deleted User'
                                : sellerDoc?.username}
                            </h3>
                            {(sellerDoc?.is_verified ||
                              hasSupportPermission(sellerDoc?.uid)) && (
                              <VerifiedShieldIcon className="h-7 w-7 shrink-0" />
                            )}
                          </div>
                          {sellerFullDoc &&
                            sellerDoc?.username.toLowerCase() !== 'support' &&
                            (sellerFullDoc.name ||
                              sellerFullDoc.first ||
                              sellerFullDoc.last) && (
                              <div className="text-[1.3rem] text-zinc-600">
                                {sellerFullDoc.name ||
                                  [sellerFullDoc.first, sellerFullDoc.last]
                                    .filter(Boolean)
                                    .join(' ')}
                              </div>
                            )}
                          <StatusLabel
                            status={sellerStatus}
                            hide_status={!!sellerDoc?.hide_status}
                          />
                        </div>
                      )}
                    </Link>

                    {/* Buyer Section */}
                    <Link
                      href={`/profile/${buyerDoc?.username_slug}`}
                      target="_blank"
                      className="group flex items-center gap-4"
                      aria-label="View buyer profile"
                    >
                      {isBuyerLoading || !buyerDoc ? (
                        <div className="h-24 w-24 animate-pulse rounded-full bg-zinc-100" />
                      ) : (
                        <Avatar user={buyerDoc} circle className="h-24 w-24" />
                      )}

                      {isBuyerLoading ? (
                        <div className="flex flex-col gap-4">
                          <div className="h-4 w-48 animate-pulse rounded-lg bg-zinc-200" />
                          <div className="flex items-center gap-1">
                            <div className="h-5 w-5 animate-pulse rounded-full bg-zinc-200" />
                            <div className="h-4 w-64 animate-pulse rounded-lg bg-zinc-200" />
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col gap-y-2">
                          <div className="flex items-center gap-2">
                            <span className="text-[1.4rem] font-medium text-zinc-500">
                              Buyer:
                            </span>
                            <h3
                              className={cn(
                                'max-w-[30rem] truncate text-left text-[1.6rem] font-semibold underline',
                                buyerDoc?.username.startsWith('deleted-') &&
                                  'text-red-600'
                              )}
                            >
                              {buyerDoc?.username.startsWith('deleted-')
                                ? 'Deleted User'
                                : buyerDoc?.username}
                            </h3>
                            {(buyerDoc?.is_verified ||
                              hasSupportPermission(buyerDoc?.uid)) && (
                              <VerifiedShieldIcon className="h-7 w-7 shrink-0" />
                            )}
                          </div>
                          {buyerFullDoc &&
                            buyerDoc?.username.toLowerCase() !== 'support' &&
                            (buyerFullDoc.name ||
                              buyerFullDoc.first ||
                              buyerFullDoc.last) && (
                              <div className="text-[1.3rem] text-zinc-600">
                                {buyerFullDoc.name ||
                                  [buyerFullDoc.first, buyerFullDoc.last]
                                    .filter(Boolean)
                                    .join(' ')}
                              </div>
                            )}
                          <StatusLabel
                            status={buyerStatus}
                            hide_status={!!buyerDoc?.hide_status}
                          />
                        </div>
                      )}
                    </Link>
                  </div>
                ) : (
                  <Link
                    href={`/profile/${otherPartyUser?.username_slug}`}
                    target="_blank"
                    className="flex flex-1 flex-col gap-y-8"
                    aria-label="View conversation details"
                  >
                    <div className="group flex items-center gap-4">
                      {isSellerLoading ||
                      isBuyerLoading ||
                      !sellerDoc ||
                      !buyerDoc ? (
                        <div className="h-24 w-24 animate-pulse rounded-full bg-zinc-100" />
                      ) : (
                        <>
                          {isSupportChat && !isLoggedInAsSupport ? (
                            <div className="h-24 w-24 rounded-full bg-black p-4">
                              <LogoWithoutText
                                className="h-full w-full"
                                fill="white"
                              />
                            </div>
                          ) : (
                            <Avatar
                              user={
                                realtimeChat?.seller_id === userDoc?.uid
                                  ? buyerDoc
                                  : sellerDoc
                              }
                              circle
                              className="h-24 w-24"
                            />
                          )}
                        </>
                      )}

                      {isSellerLoading ? (
                        <div className="flex flex-col gap-4">
                          <div className="h-4 w-48 animate-pulse rounded-lg bg-zinc-200" />

                          <div className="flex items-center gap-1">
                            <div className="h-5 w-5 animate-pulse rounded-full bg-zinc-200" />

                            <div className="h-4 w-64 animate-pulse rounded-lg bg-zinc-200" />
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col gap-y-2">
                          <div className="flex items-center gap-2">
                            {isSupportChat && !isLoggedInAsSupport ? (
                              <h3 className="text-[1.6rem] font-semibold">
                                MXLocker Support
                              </h3>
                            ) : (
                              <h3
                                className={cn(
                                  'max-w-[30rem] truncate text-left text-[1.6rem] font-semibold underline',
                                  isTalkingToDeletedUser && 'text-red-600'
                                )}
                              >
                                {isTalkingToDeletedUser
                                  ? 'Deleted User'
                                  : otherPartyUser?.username}
                              </h3>
                            )}
                            {(otherPartyUser?.is_verified ||
                              hasSupportPermission(otherPartyUser?.uid)) && (
                              <VerifiedShieldIcon className="h-7 w-7 shrink-0" />
                            )}
                          </div>

                          {isSupportChat && !isLoggedInAsSupport ? (
                            <span className="text-[1.5rem] font-medium text-zinc-500">
                              Our agents typically answer within 24 hours
                            </span>
                          ) : (
                            <StatusLabel
                              status={status}
                              hide_status={!!otherPartyUser?.hide_status}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </Link>
                )}
              </div>
              <div className="flex items-center gap-4">
                <ChatOfferButton
                  chat={realtimeChat}
                  product={product}
                  isSupportChat={isSupportChat}
                  className={isSidebar ? 'hidden' : 'hidden sm:block'}
                />
                <button
                  type="button"
                  title="Favorite Chat"
                  className="rounded-full p-2 text-black transition-colors hover:bg-zinc-100"
                  onClick={() => {
                    starChat.mutate(realtimeChat.id);
                  }}
                >
                  {userDoc?.uid &&
                  realtimeChat.favorited?.includes(userDoc?.uid) ? (
                    <StarIcon fill="black" className="h-8 w-8" />
                  ) : (
                    <StarIcon fill="none" className="h-8 w-8" />
                  )}
                </button>
                <button
                  type="button"
                  title="More Options"
                  className="rounded-full p-2 text-black transition-colors hover:bg-zinc-100"
                  onClick={() => {
                    if (isMobileDevice) {
                      setShowMobileChatDetailsModal(true);
                    } else {
                      setShowChatDetailsModal(true);
                    }
                  }}
                >
                  <MoreHorizontalIcon className="h-8 w-8" />
                </button>
              </div>
            </div>

            {/* Support Actions */}
            {(!!userDoc?.roles?.support ||
              canUseRequestPayment(userDoc?.uid)) && (
              <div className="mt-4 px-2 pb-2">
                <SupportActionsRenderer
                  layout={isSidebar ? 'dropdown' : 'horizontal'}
                  userDoc={userDoc}
                  recipientDoc={otherPartyUser}
                  chat={realtimeChat}
                  setChat={() => {}}
                  isAdmin={userDoc?.roles?.admin || false}
                  isSupport={
                    userDoc?.roles?.support || userDoc?.roles?.admin || false
                  }
                />
              </div>
            )}

            {(showSupportPreviewMessages || showBootTradeInPreviewMessages) && (
              <div className="mt-4 px-2 pb-2">
                <button
                  type="button"
                  className="rounded-full border-[1px] border-zinc-200 bg-gray-100 px-6 py-3 text-[1.4rem] font-medium transition-colors duration-75 ease-linear hover:bg-zinc-200"
                  onClick={() => setShowSupportPreviewModal(true)}
                >
                  Quick Messages
                </button>
              </div>
            )}
          </div>

          {/* Support Status Dropdowns */}
          {(userDoc?.roles?.admin || userDoc?.roles?.support) &&
            realtimeChat.support_state !== undefined && (
              <SupportStatusBar chat={realtimeChat} />
            )}

          {/* Top Section - Mobile Chat Preview (shown on mobile OR in sidebar mode) */}
          <div className={cn('mt-4 flex', isSidebar ? '' : 'sm:hidden')}>
            <MobileChatPreview
              product={product}
              offer={offer}
              order={order}
              userDoc={userDoc}
              isOutgoingOffer={
                !!userDoc?.uid &&
                getLastOfferSenderId(realtimeChat.messages) === userDoc.uid
              }
              isLoading={isProductLoading || isOfferLoading || isOrderLoading}
              action={
                <ChatOfferButton
                  chat={realtimeChat}
                  product={product}
                  isSupportChat={isSupportChat}
                />
              }
            />
          </div>

          {/* Messages */}
          <div
            ref={messageWindowRef}
            className="flex w-full flex-1 overflow-y-auto px-8 pt-8"
          >
            <div className="flex w-full grow flex-col justify-start gap-6 pb-32 sm:pb-8">
              <div className="mx-auto flex items-center justify-center gap-[1.6rem] rounded-[2rem] bg-zinc-100 px-6 py-[1.6rem] text-[1.4rem]">
                <div>
                  <LogoWithoutText className="h-16 w-16 lg:h-[2.8rem] lg:w-[2.8rem]" />
                </div>

                {isVehicleCategory(product?.category)
                  ? `MX Locker is not responsible for the sale of vehicles. Beware of scammers when selling outside the platform.`
                  : `To protect your payment, always communicate and pay through the MX Locker website or app.`}
              </div>

              <div className="h-full w-full pt-8">
                <div className="w-full pb-8">
                  {messages?.map((message, i) => {
                    const showDate =
                      i === 0 ||
                      new Date(message.created_at).toLocaleDateString() !==
                        new Date(
                          messages[i - 1].created_at
                        ).toLocaleDateString();

                    return (
                      <React.Fragment
                        key={`${message.created_at}-${message.uid}-${i}`}
                      >
                        {showDate && (
                          <div className="flex w-full items-center justify-center py-8">
                            <p className="sticky top-5 self-center text-[1.5rem] text-zinc-500">
                              {new Date(message.created_at).toLocaleDateString(
                                undefined,
                                {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                }
                              )}
                            </p>
                          </div>
                        )}

                        <div
                          className="mb-4"
                          ref={(el) => {
                            messageRefs.current[i] = el;
                          }}
                        >
                          <MessageBubble
                            chat={realtimeChat}
                            chatMessage={message}
                            realtimeMessages={messages}
                            onImageClick={onImageClick}
                            offer={offer}
                            isSidebar={isSidebar}
                            onQuickReply={handleQuickReply}
                          />
                        </div>
                      </React.Fragment>
                    );
                  })}

                  {/* Uploading image skeletons */}
                  {uploadingImages.map((uploadingImg) => (
                    <div
                      key={uploadingImg.id}
                      className="mb-4 flex justify-end"
                    >
                      <div className="flex max-w-[30rem] flex-col items-end gap-[1.6rem] justify-self-end font-medium sm:max-w-[50rem]">
                        <MessageImageSkeleton />
                      </div>
                    </div>
                  ))}

                  {/* Pending AI approval bubble */}
                  {pendingApproval && (
                    <PendingApprovalBubble
                      approval={pendingApproval}
                      onDismiss={() => setPendingApproval(null)}
                      onRetryResult={setPendingApproval}
                    />
                  )}

                  <div aria-hidden className="h-[10rem] w-full sm:hidden" />
                </div>
              </div>
            </div>
          </div>

          {/* Chat Input Container */}
          <div className="flex-shrink-0">
            <ChatInput
              ref={chatInputRef}
              message={inputMessage}
              setMessage={setInputMessage}
              onSend={handleSendMessage}
              onAttachment={handleAttachment}
              placeholder="Write your message here..."
              fileList={fileList}
              onFileListChange={setFileList}
              convertingImages={convertingImages}
              uploadingImages={uploadingImages.length > 0}
              onImageClick={handlePreviewImageClick}
              chatId={realtimeChat.id}
              onDropFiles={processFiles}
              isSupport={userDoc?.roles?.support}
              hasPendingApproval={!!pendingApproval}
              onSuggestion={setPendingApproval}
              onReword={setInputMessage}
            />
          </div>

          <input
            id={`file-input-${inputId.current}`}
            type="file"
            accept="image/*,video/*"
            className="hidden"
            multiple={true}
            onChange={handleFileChange}
          />
        </motion.div>

        {/* Right Column - Preview Sidebar */}
        {!isSidebar && (
          <div className="hidden sm:flex">
            <ChatPreviewSidebar
              product={product}
              offer={offer}
              userDoc={userDoc}
              order={order}
              isFirstTimeLoading={isFirstTimeLoading}
              chat={realtimeChat}
              isLoading={isProductLoading || isOfferLoading || isOrderLoading}
            />
          </div>
        )}

        <ChatDetailsModal
          key={realtimeChat.id}
          open={showChatDetailsModal}
          dismiss={() => setShowChatDetailsModal(false)}
          images={chatImages}
          chat={realtimeChat}
          userDoc={userDoc}
          sellerDoc={sellerDoc}
          buyerDoc={buyerDoc}
          onArchiveSuccess={onChatArchived}
        />

        <MobileChatDetailsModal
          show={showMobileChatDetailsModal}
          dismiss={() => setShowMobileChatDetailsModal(false)}
          images={chatImages}
          chat={realtimeChat}
          userDoc={userDoc}
          onArchiveSuccess={onChatArchived}
          sellerDoc={sellerDoc}
          buyerDoc={buyerDoc}
        />

        {imageModal.isOpen && (
          <ProductViewerModal
            isOpen={imageModal.isOpen}
            dismiss={() => setImageModal({ ...imageModal, isOpen: false })}
            images={imageModal.images}
            initialImage={imageModal.index}
          />
        )}

        {/* Support Preview Messages Modal */}
        <BaseModal
          isOpen={showSupportPreviewModal}
          dismiss={() => setShowSupportPreviewModal(false)}
          title="Quick Messages"
        >
          <div className="flex max-h-[60vh] flex-col gap-4 overflow-y-auto p-6">
            {showBootTradeInPreviewMessages ? (
              <>
                <button
                  type="button"
                  className="w-full rounded-full border-[1px] border-zinc-200 bg-gray-100 p-4 text-left text-[1.4rem] transition-colors duration-75 ease-linear hover:bg-zinc-200"
                  onClick={() => {
                    setInputMessage('How much credit are you looking for?');
                    setShowSupportPreviewModal(false);
                  }}
                >
                  How much credit are you looking for?
                </button>
                <button
                  type="button"
                  className="w-full rounded-full border-[1px] border-zinc-200 bg-gray-100 p-4 text-left text-[1.4rem] transition-colors duration-75 ease-linear hover:bg-zinc-200"
                  onClick={() => {
                    setInputMessage('What boots do you want to go into?');
                    setShowSupportPreviewModal(false);
                  }}
                >
                  What boots do you want to go into?
                </button>
                <button
                  type="button"
                  className="w-full rounded-full border-[1px] border-zinc-200 bg-gray-100 p-4 text-left text-[1.4rem] transition-colors duration-75 ease-linear hover:bg-zinc-200"
                  onClick={() => {
                    setInputMessage(
                      'How much credit are you looking for? What boots do you want to go into?'
                    );
                    setShowSupportPreviewModal(false);
                  }}
                >
                  How much credit are you looking for? What boots do you want to
                  go into?
                </button>
                <button
                  type="button"
                  className="w-full rounded-full border-[1px] border-zinc-200 bg-gray-100 p-4 text-left text-[1.4rem] transition-colors duration-75 ease-linear hover:bg-zinc-200"
                  onClick={() => {
                    setInputMessage(
                      'Please confirm once paid, need full name and address.'
                    );
                    setShowSupportPreviewModal(false);
                  }}
                >
                  Please confirm once paid, need full name and address.
                </button>
                <button
                  type="button"
                  className="w-full rounded-full border-[1px] border-zinc-200 bg-gray-100 p-4 text-left text-[1.4rem] transition-colors duration-75 ease-linear hover:bg-zinc-200"
                  onClick={() => {
                    setInputMessage(
                      'Mind leaving us a review? https://www.trustpilot.com/review/mxlocker.com'
                    );
                    setShowSupportPreviewModal(false);
                  }}
                >
                  Mind leaving us a review?
                  https://www.trustpilot.com/review/mxlocker.com
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="w-full rounded-full border-[1px] border-zinc-200 bg-gray-100 p-4 text-left text-[1.4rem] transition-colors duration-75 ease-linear hover:bg-zinc-200"
                  onClick={() => {
                    setInputMessage(
                      'Thank you for confirming this with us, our team will monitor the return shipment.'
                    );
                    setShowSupportPreviewModal(false);
                  }}
                >
                  Thank you for confirming this with us, our team will monitor
                  the return shipment.
                </button>
                <button
                  type="button"
                  className="w-full rounded-full border-[1px] border-zinc-200 bg-gray-100 p-4 text-left text-[1.4rem] transition-colors duration-75 ease-linear hover:bg-zinc-200"
                  onClick={() => {
                    setInputMessage(
                      'The order status has now been updated, is there anything else we can assist you with?'
                    );
                    setShowSupportPreviewModal(false);
                  }}
                >
                  The order status has now been updated, is there anything else
                  we can assist you with?
                </button>
                <button
                  type="button"
                  className="w-full rounded-full border-[1px] border-zinc-200 bg-gray-100 p-4 text-left text-[1.4rem] transition-colors duration-75 ease-linear hover:bg-zinc-200"
                  onClick={() => {
                    setInputMessage(
                      'Will you provide us with photos of the item you received that arrived not as described?'
                    );
                    setShowSupportPreviewModal(false);
                  }}
                >
                  Will you provide us with photos of the item you received that
                  arrived not as described?
                </button>
                <button
                  type="button"
                  className="w-full rounded-full border-[1px] border-zinc-200 bg-gray-100 p-4 text-left text-[1.4rem] transition-colors duration-75 ease-linear hover:bg-zinc-200"
                  onClick={() => {
                    setInputMessage(
                      'Following up here, we can see the latest update from the shipping carrier is below. Our team will continue to closely monitor this for you and we apologize for the delay caused here.'
                    );
                    setShowSupportPreviewModal(false);
                  }}
                >
                  Following up here, we can see the latest update from the
                  shipping carrier is below. Our team will continue to closely
                  monitor this for you and we apologize for the delay caused
                  here.
                </button>
                <button
                  type="button"
                  className="w-full rounded-full border-[1px] border-zinc-200 bg-gray-100 p-4 text-left text-[1.4rem] transition-colors duration-75 ease-linear hover:bg-zinc-200"
                  onClick={() => {
                    setInputMessage(
                      'We appreciate you being apart of the MX Locker community. Do you mind leaving us a review at https://www.trustpilot.com/review/mxlocker.com .'
                    );
                    setShowSupportPreviewModal(false);
                  }}
                >
                  We appreciate you being apart of the MX Locker community. Do
                  you mind leaving us a review at
                  https://www.trustpilot.com/review/mxlocker.com .
                </button>
              </>
            )}
          </div>
        </BaseModal>

        {/* Drag and Drop Overlay */}
        {isDragging && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-zinc-50/80 backdrop-blur-sm">
            <div className="flex flex-col items-center justify-center gap-6 rounded-3xl border-2 border-dashed border-zinc-200 bg-white/90 px-12 py-16 text-center shadow-xl">
              <div className="flex h-24 w-24 items-center justify-center rounded-full bg-red-100">
                <CloudUploadIcon className="h-12 w-12 text-brand-secondary" />
              </div>
              <div>
                <h3 className="text-2xl font-semibold text-gray-900">
                  Drop media here
                </h3>
                <p className="mt-2 text-gray-600">
                  Release to upload images or videos to the chat
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </LayoutGroup>
  );
}
